import { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { api, formatErr } from '@/lib/api';
import { toast } from 'sonner';
import { Phone, Square, Play, Clock } from 'lucide-react';

const OUTCOMES = [
  { key: 'connected', label: 'Connected', color: 'bg-emerald-500 text-white', hover: 'hover:bg-emerald-600' },
  { key: 'not_answered', label: 'Not Answered', color: 'bg-amber-500 text-white', hover: 'hover:bg-amber-600' },
  { key: 'callback', label: 'Callback', color: 'bg-blue-500 text-white', hover: 'hover:bg-blue-600' },
  { key: 'not_interested', label: 'Not Interested', color: 'bg-red-500 text-white', hover: 'hover:bg-red-600' },
];

function isoToLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function localInputToIso(v) { return v ? new Date(v).toISOString() : ''; }
function fmtDuration(sec) {
  sec = Math.max(0, Math.floor(sec || 0));
  const m = Math.floor(sec / 60), s = sec % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}
const isMobile = () => typeof navigator !== 'undefined' && /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);

export default function CallLogDialog({ open, onOpenChange, candidate, candidateType = 'call_candidate', onSaved, defaultDialer = false }) {
  const [startedAt, setStartedAt] = useState('');
  const [endedAt, setEndedAt] = useState('');
  const [running, setRunning] = useState(false);
  const [tickSec, setTickSec] = useState(0);
  const [outcome, setOutcome] = useState('connected');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const tickerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const now = new Date();
    setStartedAt(now.toISOString());
    setEndedAt(''); setTickSec(0); setOutcome('connected'); setNotes('');
    setRunning(true);
  }, [open]);

  useEffect(() => {
    if (!running) { if (tickerRef.current) clearInterval(tickerRef.current); return; }
    tickerRef.current = setInterval(() => {
      if (!startedAt) return;
      setTickSec(Math.round((Date.now() - new Date(startedAt).getTime()) / 1000));
    }, 500);
    return () => tickerRef.current && clearInterval(tickerRef.current);
  }, [running, startedAt]);

  const stopTimer = () => {
    const now = new Date();
    setEndedAt(now.toISOString()); setRunning(false);
    if (startedAt) setTickSec(Math.round((now.getTime() - new Date(startedAt).getTime()) / 1000));
  };
  const resumeTimer = () => { setEndedAt(''); setRunning(true); };

  const computedDuration = (() => {
    if (running) return tickSec;
    if (startedAt && endedAt) return Math.max(0, Math.round((new Date(endedAt).getTime() - new Date(startedAt).getTime()) / 1000));
    return 0;
  })();

  const dialPhone = () => {
    if (!candidate?.phone) return;
    // Works on all devices: on mobile it opens dialer, on desktop it triggers OS handler / no-op gracefully.
    window.location.href = `tel:${candidate.phone}`;
  };

  const save = async () => {
    if (!candidate?.id) { toast.error('No candidate selected'); return; }
    if (!startedAt) { toast.error('Start time required'); return; }
    const finalEnded = endedAt || new Date().toISOString();
    const duration = Math.max(0, Math.round((new Date(finalEnded).getTime() - new Date(startedAt).getTime()) / 1000));
    setSaving(true);
    try {
      const { data } = await api.post('/call-logs', {
        candidate_type: candidateType,
        candidate_id: candidate.id,
        candidate_name: candidate.name, candidate_phone: candidate.phone,
        started_at: startedAt, ended_at: finalEnded, duration_seconds: duration,
        outcome, notes,
      });
      toast.success('Call logged'); onSaved?.(data); onOpenChange(false);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="call-log-dialog"
        className="w-[calc(100vw-1rem)] max-w-[calc(100vw-1rem)] sm:max-w-lg h-[92vh] sm:h-auto sm:max-h-[92vh] overflow-y-auto p-4 sm:p-6"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-xl sm:text-2xl font-black flex items-center gap-2">
            <Phone className="w-5 h-5 text-primary" /> Log a call
          </DialogTitle>
          <div className="text-sm text-muted-foreground mt-1">
            <div className="font-medium text-foreground truncate">{candidate?.name || '—'}</div>
            {candidate?.phone && (
              <a href={`tel:${candidate.phone}`} data-testid="call-dialer-link" className="text-primary hover:underline text-base font-mono">{candidate.phone}</a>
            )}
          </div>
        </DialogHeader>

        {/* Big DIAL button — the primary mobile action */}
        {candidate?.phone && (
          <Button
            type="button"
            onClick={dialPhone}
            data-testid="call-dial-btn"
            className="w-full h-14 text-base bg-emerald-500 hover:bg-emerald-600 text-white shadow-md active:scale-[0.99] transition-transform"
          >
            <Phone className="w-5 h-5 mr-2" /> Tap to dial {candidate.phone}
          </Button>
        )}

        {/* Timer card */}
        <div className="tactical-card p-4 bg-primary/5 border-primary/30 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`w-10 h-10 rounded-md grid place-items-center flex-shrink-0 ${running ? 'bg-emerald-500/15 text-emerald-500 animate-pulse' : 'bg-muted text-muted-foreground'}`}>
              <Clock className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Duration</div>
              <div className="font-display text-2xl sm:text-3xl font-black tabular-nums" data-testid="call-timer">{fmtDuration(computedDuration)}</div>
            </div>
          </div>
          {running ? (
            <Button variant="outline" onClick={stopTimer} data-testid="call-stop" className="h-11"><Square className="w-4 h-4 mr-2" />Stop</Button>
          ) : (
            <Button variant="outline" onClick={resumeTimer} data-testid="call-resume" className="h-11"><Play className="w-4 h-4 mr-2" />Resume</Button>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Started</Label>
            <Input type="datetime-local" value={isoToLocalInput(startedAt)} onChange={(e) => setStartedAt(localInputToIso(e.target.value))} data-testid="call-started-at" className="h-11" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest">Ended</Label>
            <Input type="datetime-local" value={isoToLocalInput(endedAt)} onChange={(e) => { setEndedAt(localInputToIso(e.target.value)); setRunning(false); }} data-testid="call-ended-at" className="h-11" />
          </div>
        </div>

        <div>
          <Label className="text-[10px] uppercase tracking-widest">Outcome</Label>
          <div className="grid grid-cols-2 gap-2 mt-1" data-testid="call-outcome-grid">
            {OUTCOMES.map(o => (
              <button
                key={o.key} type="button"
                data-testid={`call-outcome-${o.key}`}
                onClick={() => setOutcome(o.key)}
                className={`h-12 px-3 rounded-md border text-sm font-medium transition-colors ${outcome === o.key ? `${o.color} ${o.hover} border-transparent` : 'border-border hover:border-primary/60 text-foreground bg-background'}`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label className="text-[10px] uppercase tracking-widest">Notes</Label>
          <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="What did they say? Next steps?" data-testid="call-notes" />
        </div>

        <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end sticky bottom-0 bg-card pt-2 -mx-4 sm:mx-0 px-4 sm:px-0">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="call-cancel" className="h-11">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90 text-white h-11" data-testid="call-save">
            {saving ? 'Saving…' : 'Save call log'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
